import { useCallback, useEffect, useMemo } from "react";

import { modules, useObjectMemo, useFeature } from "byzantine";
import {
  WireTypeEnum,
  type WireType,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import { useNavigate } from "react-router-dom";

export type Method = "none" | "freeform" | "template";

const REQUIRED_FEATURE = Object.freeze({
  international: {
    freeform: "indigo.SwiftWireTransaction_permission",
    template: "indigo.SwiftWireViaTemplate_permission",
  },
  domestic: {
    freeform: "indigo.WireTransaction_permission",
    template: "indigo.FedwireViaTemplate_permission",
  },
});

export const useFormContainer = (wireType: WireType) => {
  const { submitMethod, method, form } =
    modules.wireTemplates.useMethodSelector();
  const { featureEnabled } = useFeature();

  const options = useMemo(() => {
    const effectiveWireType =
      wireType === WireTypeEnum.None ? WireTypeEnum.Domestic : wireType;
    const allOptions = modules.wireTemplates.METHOD_OPTIONS;
    const hasFreeform = featureEnabled(
      REQUIRED_FEATURE[effectiveWireType].freeform
    );
    const hasTemplate = featureEnabled(
      REQUIRED_FEATURE[effectiveWireType].template
    );

    return allOptions.filter(
      (o) =>
        (o.id === "freeform" && hasFreeform) ||
        (o.id === "template" && hasTemplate)
    );
  }, [wireType, featureEnabled]);

  const singleMethod = options.length === 1 ? options[0].id : null;

  useEffect(() => {
    if (singleMethod) {
      form.setFieldValue("method", singleMethod, false);
    }
  }, [singleMethod, form]);

  const navigate = useNavigate();
  const onCancel = useCallback(() => {
    navigate("/transfer");
  }, [navigate]);

  const onSubmitMethod = useCallback(async () => {
    await submitMethod();
  }, [submitMethod]);

  return useObjectMemo({
    method,
    onCancel,
    onSubmitMethod,
    form,
    options,
  });
};
